import { useNavigate } from 'react-router-dom';

// VideoMenuUnderlay.js — REPLACE ENTIRE FILE
import React, { useEffect, useRef, useState } from 'react';

export default function VideoMenuUnderlay({ scene, clips, onLoopStart, onDebugUpdate }) {

const lastLocationRef = useRef(null);
const currentLocationRef = useRef(null);
const currentLoopRef = useRef(null);
const segmentsLeftRef = useRef(0);
const inTransitRef = useRef(false);

const emitDebug = () => {
  if (typeof onDebugUpdate === 'function') {
    onDebugUpdate({
      from: lastLocationRef.current,
      inTransit: inTransitRef.current,
      currentLoop: currentLoopRef.current,
      currentLocation: currentLocationRef.current,
      segmentsLeft: segmentsLeftRef.current,
    });
  }
};



// --- Speed compensation (simple lookup) ---
const SPEED_LUT = {
  // distance (s) from edge -> playbackRate
  0.0: 1.50,
  0.1: 1.35,
  0.2: 1.25,
  0.3: 1.15
};
const EDGE_WINDOW_S = 0.3; // only affect within this many seconds of start/end

  const __lutTimers = new WeakMap();

  function _lookupRate(dt) {
    // dt = distance (s) to nearest edge; snap to 0.1s bucket
    const bucket = Math.max(0, Math.round(dt * 10) / 10);
    return SPEED_LUT.hasOwnProperty(bucket) ? SPEED_LUT[bucket] : 1.0;
  }

  function startTransitionLUT(el) {
    stopTransitionLUT(el);
    if (!el) return;
    const tick = () => {
      const d = el.duration || 0;
      const t = el.currentTime || 0;
      if (d > 0) {
        const distEdge = Math.min(t, Math.max(0, d - t)); // seconds to nearest edge
        el.playbackRate = distEdge <= EDGE_WINDOW_S ? _lookupRate(distEdge) : 1.0;
      } else {
        el.playbackRate = 1.0;
      }
    };
    // 60ms is enough; keep it cheap and stable across browsers
    const id = setInterval(tick, 60);
    __lutTimers.set(el, id);
    tick(); // prime once
  }

  function stopTransitionLUT(el) {
    const id = __lutTimers.get(el);
    if (id) clearInterval(id);
    __lutTimers.delete(el);
    if (el) el.playbackRate = 1.0;
  }

  //
  //
  // --- DEBUG TOGGLE ---
  const DEBUG = true; // set to false to silence logs
const TRANSITION_RATE = 1.25; // 3× speed for transitions

  const log = (...args) => { if (DEBUG) console.log('[VideoMenuUnderlay]', ...args); };
  const logErr = (...args) => { if (DEBUG) console.error('[VideoMenuUnderlay]', ...args); };
  const navigate = useNavigate();

  // Double-buffered videos (A = active, B = buffer)
  const videoARef = useRef(null);
  const videoBRef = useRef(null);
  const activeIdxRef = useRef(0); // 0 => A is active, 1 => B is active
  const V = () => (activeIdxRef.current === 0 ? videoARef.current : videoBRef.current);
  const B = () => (activeIdxRef.current === 0 ? videoBRef.current : videoARef.current);

  // Previous scene and autoplay unlock
  const prevSceneRef = useRef(scene);
  const [unlocked, setUnlocked] = useState(false);

  // Loop + transitions control
  const loopIdxRef = useRef(0);
  const loopListRef = useRef([]);
  const isLoopingRef = useRef(false);
  const pendingTransitionRef = useRef(null);

  const asArray = (val) => (Array.isArray(val) ? val : (val ? [val] : []));

  const safePlay = (el, label) => {
    try {
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.then(() => log('play() resolved:', label, 'src=', el.currentSrc))
         .catch((e) => logErr('play() rejected:', label, e?.message || e, 'src=', el.currentSrc));
      }
    } catch (e) {
      logErr('play() threw:', label, e?.message || e);
    }
  };

  // Preload into a hidden <video> element, then invoke onReady
  const preload = (el, src, onReady) => {
    if (!el) return;
    const done = () => { el.removeEventListener('canplay', done); onReady && onReady(); };
    el.addEventListener('canplay', done, { once: true });
    el.src = src;
    el.load();
    // If already buffered, complete immediately
    if (el.readyState >= 2) { done(); }
  };

  // Instant layer swap (no src change on visible layer)
  const swapLayers = () => {
    const a = V(), b = B();
    if (!a || !b) return;
    a.style.opacity = '0';
    b.style.opacity = '1';
    activeIdxRef.current = 1 - activeIdxRef.current;
  };

  // ----- Transitions (buffered) -----
  const runPendingTransitions = () => {
    const p = pendingTransitionRef.current;
    if (!p) return;
    pendingTransitionRef.current = null;
    isLoopingRef.current = false;
    log('running pending transitions →', p);

    // const playTransitionIdx = (k) => {
    //   if (k >= p.sources.length) {
    //     // all transitions finished → start dest loop
    //     playLoop(p.destScene);

    //     const src = p.sources[k];
    //     segmentsLeftRef.current = p.sources.length - k - 1;
    //     currentLoopRef.current = src;
    //     emitDebug();


    //     return;
    //   }
    //   const src = p.sources[k];
    //   preload(B(), src, () => {
    //     swapLayers();
    //     const vNow = V();
    //     vNow.playbackRate = TRANSITION_RATE; // ← speed up transitions        
    //     vNow.loop = false;
    //     vNow.onended = () => playTransitionIdx(k + 1);
    //     safePlay(vNow, `transition-${k}`);
    //   });
    // };
  const playTransitionIdx = (k) => {
    if (k >= p.sources.length) {
      segmentsLeftRef.current = 0;
      currentLoopRef.current = null;
      emitDebug();
      playLoop(p.destScene);
      return;
    }

    const src = p.sources[k];
    segmentsLeftRef.current = p.sources.length - k - 1; // segments left *after* this one
    currentLoopRef.current = src;
    emitDebug();

    preload(B(), src, () => {
      swapLayers();
      const vNow = V();
      vNow.playbackRate = TRANSITION_RATE; // ← speed up transitions
      vNow.loop = false;
      vNow.onended = () => playTransitionIdx(k + 1);
      safePlay(vNow, `transition-${k}`);
    });
  };

    // kick off first transition
    playTransitionIdx(0);
  };

  // ----- Loop playback (buffered) -----
  const playLoop = (targetScene) => {


    inTransitRef.current = false;
    currentLocationRef.current = targetScene;
    lastLocationRef.current = targetScene;  
    segmentsLeftRef.current = 0;

    const list = asArray(clips?.loops?.[targetScene]);
    loopListRef.current = list.length ? list : [''];
    loopIdxRef.current = 0;
    isLoopingRef.current = true;

    log('playLoop:', targetScene, 'sources=', loopListRef.current);

    // Preload first loop segment on buffer, then swap in instantly
    const firstSrc = loopListRef.current[0];

    const primeNextLoop = () => {
      const nextIdx = (loopIdxRef.current + 1) % loopListRef.current.length;
      const nextSrc = loopListRef.current[nextIdx];
      if (nextSrc) preload(B(), nextSrc);
    };

    const advance = () => {
      if (pendingTransitionRef.current) {
        log('loop boundary reached; pendingTransition detected, executing…');
        runPendingTransitions();
        return;
      }
      loopIdxRef.current = (loopIdxRef.current + 1) % loopListRef.current.length;
      // The next segment should already be preloaded on the buffer → swap instantly
      swapLayers();
      const vNow = V();
      vNow.playbackRate = 1;   // ← ensure loops are normal speed      
      vNow.loop = false; // manual loop to keep boundaries observable
      vNow.onended = advance;
      if (typeof onLoopStart === 'function') onLoopStart(targetScene); // notify immediately
      safePlay(vNow, 'loop-advance');
      const nextIdx = (loopIdxRef.current + 1) % loopListRef.current.length;
      const nextSrc = loopListRef.current[nextIdx];
      currentLoopRef.current = nextSrc;
      emitDebug();


      primeNextLoop(); // preload following segment
    };

    preload(B(), firstSrc, () => {
      swapLayers();

      const vNow = V();
      vNow.playbackRate = 1;   // ← ensure loops are normal speed      
      vNow.loop = false;
      vNow.onended = advance;
      if (typeof onLoopStart === 'function') onLoopStart(targetScene); // notify immediately
      log('loop start →', vNow.currentSrc || firstSrc);
      const firstSrc = loopListRef.current[0];
      currentLoopRef.current = firstSrc;
      emitDebug();


      safePlay(vNow, 'loop-start');
      primeNextLoop();
    });
  };

  // ----- Debug: attach events on both layers (optional) -----
  useEffect(() => {
    if (!DEBUG) return;
    const attach = (el, tag) => {
      if (!el) return () => {};
      const onEvent = (e) => {
        const info = {
          tag,
          type: e.type,
          currentSrc: el.currentSrc,
          readyState: el.readyState,
          networkState: el.networkState,
          paused: el.paused,
          ended: el.ended,
          muted: el.muted,
          volume: el.volume,
          opacity: el.style?.opacity,
        };
        if (e.type === 'error' && el.error) {
          logErr('video event:', info, 'mediaError=', { code: el.error.code, message: el.error.message });
        } else {
          log('video event:', info);
        }
      };
      const evs = [
        'loadedmetadata','canplay','canplaythrough','play','playing',
        'pause','ended','error','stalled','waiting','suspend','abort'
      ];
      evs.forEach((n) => el.addEventListener(n, onEvent));
      return () => evs.forEach((n) => el.removeEventListener(n, onEvent));
    };

    const offA = attach(videoARef.current, 'A');
    const offB = attach(videoBRef.current, 'B');
    return () => { offA && offA(); offB && offB(); };
  }, [DEBUG]);

    

// ---- Unlock autoplay on first interaction -----
useEffect(() => {
  const evs = [
    'pointerdown','keydown','mousemove','wheel',
    'touchstart','touchmove','dragstart','scroll'
  ];
  const unlock = (e) => { log('autoplay unlock via', e.type); setUnlocked(true); };
  evs.forEach((name) => window.addEventListener(name, unlock, { once: true, passive: true }));
  return () => evs.forEach((name) => window.removeEventListener(name, unlock));
}, []);


  // ----- First start: run once after unlock (do NOT depend on `scene`) -----
  useEffect(() => {
    if (!unlocked) return;
    log('first start for scene:', scene);
    playLoop(scene);
    prevSceneRef.current = scene;
  }, [unlocked]); // intentionally not depending on scene

  // ----- Scene changes: queue transitions to boundary, then run buffered, then start dest loop -----
  useEffect(() => {
    if (!unlocked) return;

    const prev = prevSceneRef.current;
    if (prev === scene) return;

    const key = `${prev}->${scene}`;
    const tList = asArray(clips?.transitions?.[key]);

    log('scene change requested:', `${prev} → ${scene}`, 'transition list size:', tList.length);

    if (tList.length) {

      lastLocationRef.current = prev;
      currentLocationRef.current = `${prev}->${scene}`;
      inTransitRef.current = true;
      segmentsLeftRef.current = tList.length;
      currentLoopRef.current = tList[0];
      emitDebug();

      if (isLoopingRef.current) {
        pendingTransitionRef.current = { destScene: scene, sources: tList };
        // prime first transition on buffer so swap is instant at boundary
        preload(B(), tList[0]);
        log('transition queued until loop boundary:', pendingTransitionRef.current);
      } else {
        // not currently looping → run immediately, buffered
        pendingTransitionRef.current = { destScene: scene, sources: tList };
        preload(B(), tList[0], () => runPendingTransitions());
      }
    } else {
  lastLocationRef.current = prev;
  currentLocationRef.current = scene;
  inTransitRef.current = false;
  segmentsLeftRef.current = 0;
  const firstLoop = clips?.loops?.[scene] ? asArray(clips.loops[scene])[0] : null;
  currentLoopRef.current = firstLoop;
  emitDebug();

      log('no transition found; switching directly to loop for', scene);
      playLoop(scene);
    }

    prevSceneRef.current = scene;
  }, [scene, unlocked, clips]);

  return (
    <>
      {/* ACTIVE LAYER */}
      <video
        ref={videoARef}
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
        style={{
          position: 'absolute',
          objectPosition: '50% 20%',    
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          pointerEvents: 'none',
          opacity: 1, // visible
        }}
      />
      {/* BUFFER LAYER */}
      <video
        ref={videoBRef}
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: '50% 20%',    
          pointerEvents: 'none',
          opacity: 0, // hidden
        }}
      />
    </>
  );
}

